import React from 'react';
import { useNavigate } from 'react-router-dom';
import { extractVideoId } from '../utils/youtubeUtils';
import { Eye, ThumbsUp, MessageCircle, Star, Trash2 } from 'react-feather';

const VideoGrid = ({
  videos,
  onToggleFavorite,
  onDelete,
  onSelectVideo,
  selectedVideos = [],
  showCheckbox = true,
  showFavorite = true,
  showDelete = true,
  canFavorite = true,
}) => {
  const navigate = useNavigate();

  const formatNumber = (num) => {
    if (!num || num === 0) return '0';
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const formatDate = (date) => {
    if (!date) return 'Unknown';
    const timestamp = typeof date === 'number' ? date : (date instanceof Date ? date.getTime() : new Date(date).getTime());
    const d = new Date(timestamp);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleVideoClick = (video) => {
    const videoId = extractVideoId(video.youtubeUrl);
    if (videoId) {
      navigate(`/video/${videoId}`);
    } else {
      // If we can't extract video ID, try to navigate with URL
      const fallbackId = video.youtubeUrl.split('v=')[1]?.split('&')[0];
      if (fallbackId) {
        navigate(`/video/${fallbackId}`);
      } else {
        navigate('/', { state: { videoUrl: video.youtubeUrl } });
      }
    }
  };

  const handleToggleFavoriteClick = (e, video) => {
    e.stopPropagation();
    if (onToggleFavorite && canFavorite) {
      onToggleFavorite(video.id, video.isFavorite || false, e);
    }
  };

  const handleDeleteClick = (e, video) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(video.id, e);
    }
  };

  const handleSelectClick = (e, videoId) => {
    e.stopPropagation();
    if (onSelectVideo && showCheckbox) {
      onSelectVideo(videoId, e);
    }
  };

  return (
    <div className="video-grid grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-5">
      {videos.map((video) => (
        <div
          key={video.id}
          className="video-card bg-white bg-opacity-5 rounded-xl overflow-hidden transition-all duration-200 cursor-pointer hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(0,0,0,0.3)]"
          onClick={() => handleVideoClick(video)}
        >
          {/* Video Thumbnail */}
          <div className="video-thumbnail relative w-full pt-[56.25%] bg-gray-800 overflow-hidden">
            {video.thumbnailUrl && (
              <img
                src={video.thumbnailUrl}
                alt={video.videoTitle || 'Video thumbnail'}
                className="absolute top-0 left-0 w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            
            {/* Checkbox */}
            {showCheckbox && (
              <input
                type="checkbox"
                className="video-checkbox absolute top-2 left-2 w-5 h-5 cursor-pointer z-10"
                checked={selectedVideos.includes(video.id)}
                onChange={(e) => handleSelectClick(e, video.id)}
                onClick={(e) => e.stopPropagation()}
              />
            )}

            {/* Action Icons */}
            <div className="video-actions absolute top-2 right-2 flex gap-2 z-10">
              {showFavorite && (
                <div
                  className={`action-icon w-8 h-8 rounded-md flex items-center justify-center transition-colors duration-200 ${
                    video.isFavorite 
                      ? 'bg-yellow-400 bg-opacity-90' 
                      : 'bg-black bg-opacity-60'
                  } ${canFavorite ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                  onClick={(e) => handleToggleFavoriteClick(e, video)}
                  title={video.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                >
                  <Star 
                    size={16} 
                    fill={video.isFavorite ? 'currentColor' : 'none'} 
                    color={video.isFavorite ? '#000' : '#fff'}
                  />
                </div>
              )}
              
              {showDelete && (
                <div
                  className="action-icon delete w-8 h-8 bg-red-500 bg-opacity-90 rounded-md flex items-center justify-center cursor-pointer transition-colors duration-200"
                  onClick={(e) => handleDeleteClick(e, video)}
                  title="Delete video"
                >
                  <Trash2 size={16} color="#fff" />
                </div>
              )}
            </div>

            {/* Duration */}
            {video.duration && (
              <div className="video-duration absolute bottom-2 right-2 bg-black bg-opacity-80 px-2 py-1 rounded text-xs font-semibold text-white">
                {video.duration}
              </div>
            )}
          </div>

          {/* Video Info */}
          <div className="video-info p-4">
            <div className="video-title text-sm font-medium leading-snug mb-2 line-clamp-2 text-white">
              {video.customName || video.videoTitle || 'Untitled Video'}
            </div>
            
            <div className="video-channel text-[13px] text-white text-opacity-60 mb-3">
              {video.channelName || 'Unknown Channel'}
            </div>
            
            <div className="video-stats flex items-center gap-3 flex-wrap mb-3">
              <div className="stat-badge flex items-center gap-1.5 bg-secondary-500 px-2.5 py-1 rounded-md text-xs font-semibold text-white">
                <MessageCircle size={12} />
                {formatNumber(video.commentCount || 0)}
              </div>
              
              {video.viewCount > 0 && (
                <div className="stat-item flex items-center gap-1 text-xs text-white text-opacity-60">
                  <Eye size={12} />
                  {formatNumber(video.viewCount)}
                </div>
              )}
              
              {video.likeCount > 0 && (
                <div className="stat-item flex items-center gap-1 text-xs text-white text-opacity-60">
                  <ThumbsUp size={12} />
                  {formatNumber(video.likeCount)}
                </div>
              )}
            </div>
            
            <div className="video-date text-xs text-white text-opacity-50">
              Analyzed: {formatDate(video.lastAnalyzedAt)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default VideoGrid;

